/**
 * DataRowBlock - Renders data-driven carousel/grid blocks
 * 
 * Supports: series_carousel, novel_carousel, artwork_gallery,
 *           continue_reading, trending_series, new_releases, featured_creators
 * 
 * Uses useDataRow hook for centralized API fetching with:
 * - Per-session cache
 * - Debounced config changes
 * - Admin-safe read-only mode
 * - Graceful error fallback
 */
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  ChevronLeft, ChevronRight, TrendingUp, Star, Clock, 
  BookOpen, Palette, Eye, ExternalLink, AlertCircle, RefreshCw
} from 'lucide-react';
import { cn } from '../../lib/utils';
import useDataRow from './useDataRow';

// ============================================================================
// SAMPLE DATA — only used when API has zero results or endpoint unavailable
// ============================================================================

const SAMPLE_SERIES = [
  { id: 's1', title: 'Midnight Chronicles', cover_url: 'https://picsum.photos/seed/s1/300/400', genre: 'Action', episodes: 45, views: 12500 },
  { id: 's2', title: 'Urban Legends', cover_url: 'https://picsum.photos/seed/s2/300/400', genre: 'Horror', episodes: 32, views: 8900 },
  { id: 's3', title: 'Love in Tokyo', cover_url: 'https://picsum.photos/seed/s3/300/400', genre: 'Romance', episodes: 28, views: 15200 },
  { id: 's4', title: 'Dragon Slayer', cover_url: 'https://picsum.photos/seed/s4/300/400', genre: 'Fantasy', episodes: 67, views: 23400 },
  { id: 's5', title: 'Street Fighter', cover_url: 'https://picsum.photos/seed/s5/300/400', genre: 'Action', episodes: 52, views: 18700 },
  { id: 's6', title: 'Mystery Manor', cover_url: 'https://picsum.photos/seed/s6/300/400', genre: 'Mystery', episodes: 24, views: 9800 },
];

const SAMPLE_ARTWORKS = [
  { id: 'a1', title: 'Sunset Dreams', image_url: 'https://picsum.photos/seed/a1/400/400', artist: 'ArtistOne', likes: 234 },
  { id: 'a2', title: 'Neon City', image_url: 'https://picsum.photos/seed/a2/400/400', artist: 'ArtistTwo', likes: 567 },
  { id: 'a3', title: 'Fantasy Forest', image_url: 'https://picsum.photos/seed/a3/400/400', artist: 'ArtistThree', likes: 891 },
  { id: 'a4', title: 'Abstract Motion', image_url: 'https://picsum.photos/seed/a4/400/400', artist: 'ArtistFour', likes: 432 },
];

const SAMPLE_CREATORS = [
  { id: 'c1', display_name: 'CreatorOne', avatar_url: 'https://picsum.photos/seed/c1/200/200', series_count: 5, followers: 12500 },
  { id: 'c2', display_name: 'CreatorTwo', avatar_url: 'https://picsum.photos/seed/c2/200/200', series_count: 8, followers: 23400 },
  { id: 'c3', display_name: 'CreatorThree', avatar_url: 'https://picsum.photos/seed/c3/200/200', series_count: 3, followers: 8900 },
  { id: 'c4', display_name: 'CreatorFour', avatar_url: 'https://picsum.photos/seed/c4/200/200', series_count: 12, followers: 45600 },
];

function getSampleData(blockType, limit) {
  if (blockType === 'artwork_gallery') return SAMPLE_ARTWORKS.slice(0, limit);
  if (blockType === 'featured_creators') return SAMPLE_CREATORS.slice(0, limit);
  return SAMPLE_SERIES.slice(0, limit);
}

// ============================================================================
// SHARED COMPONENTS
// ============================================================================

const SectionHeader = ({ title, seeAllLink, seeAllText = 'See All', dataSource, isLive }) => (
  <div className="flex items-center justify-between px-4 sm:px-6 mb-4">
    <div className="flex items-center gap-3">
      <h2 className="text-lg sm:text-xl font-bold text-white">{title}</h2>
      {isLive && (
        <span className="px-1.5 py-0.5 bg-green-500/20 text-green-400 text-[10px] font-medium rounded" data-testid="live-data-badge">
          LIVE
        </span>
      )}
    </div>
    {seeAllLink && (
      <Link 
        to={seeAllLink}
        className="text-sm text-orange-500 hover:text-orange-400 flex items-center gap-1 transition-colors"
      >
        {seeAllText}
        <ExternalLink className="w-3 h-3" />
      </Link>
    )}
  </div>
);

const ScrollContainer = ({ children, scrollRef, className }) => {
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const internalRef = useRef(null);
  const containerRef = scrollRef || internalRef;
  
  const checkScroll = () => {
    const el = containerRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
  };
  
  useEffect(() => {
    checkScroll();
    const el = containerRef.current;
    if (el) {
      el.addEventListener('scroll', checkScroll);
      window.addEventListener('resize', checkScroll);
    }
    return () => {
      el?.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, [children]);
  
  const scroll = (direction) => {
    const el = containerRef.current;
    if (!el) return;
    const scrollAmount = el.clientWidth * 0.8;
    el.scrollBy({ left: direction === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' });
  };
  
  return (
    <div className="relative group">
      {canScrollLeft && (
        <button
          onClick={() => scroll('left')}
          className="absolute left-2 top-1/2 -translate-y-1/2 z-10 
                     w-10 h-10 rounded-full bg-black/80 hover:bg-black
                     flex items-center justify-center
                     opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <ChevronLeft className="w-6 h-6 text-white" />
        </button>
      )}
      
      <div
        ref={containerRef}
        className={cn(
          "flex gap-4 overflow-x-auto scrollbar-hide px-4 sm:px-6 pb-2",
          "scroll-smooth snap-x snap-mandatory",
          className
        )}
      >
        {children}
      </div>
      
      {canScrollRight && (
        <button
          onClick={() => scroll('right')}
          className="absolute right-2 top-1/2 -translate-y-1/2 z-10 
                     w-10 h-10 rounded-full bg-black/80 hover:bg-black
                     flex items-center justify-center
                     opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <ChevronRight className="w-6 h-6 text-white" />
        </button>
      )}
    </div>
  );
};

// ============================================================================
// CARD COMPONENTS
// ============================================================================

const SeriesCard = ({ series, rank, variant = 'discovery', showRank = false }) => (
  <div
    className="flex-shrink-0 w-[140px] sm:w-[160px] snap-start group cursor-pointer"
    data-testid={`series-card-${series.id}`}
  >
    <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-2 bg-zinc-800">
      {series.cover_url ? (
        <img
          src={series.cover_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${series.cover_url}` : series.cover_url}
          alt={series.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          loading="lazy"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <BookOpen className="w-8 h-8 text-zinc-600" />
        </div>
      )}
      
      {showRank && rank && (
        <div className="absolute top-2 left-2 w-8 h-8 rounded-full bg-orange-500 
                        flex items-center justify-center text-white font-bold text-sm">
          {rank}
        </div>
      )}
      
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent 
                      opacity-0 group-hover:opacity-100 transition-opacity" />
      
      {series.genre && (
        <div className="absolute bottom-2 left-2 px-2 py-0.5 bg-black/60 rounded text-xs text-white">
          {series.genre}
        </div>
      )}
    </div>
    
    <h3 className="text-sm font-medium text-white truncate group-hover:text-orange-400 transition-colors">
      {series.title}
    </h3>
    
    <div className="flex items-center gap-2 text-xs text-zinc-500">
      {series.episodes > 0 && <span>{series.episodes} eps</span>}
      {series.views > 0 && (
        <>
          <span>·</span>
          <span className="flex items-center gap-1">
            <Eye className="w-3 h-3" />
            {series.views >= 1000 ? `${(series.views / 1000).toFixed(1)}k` : series.views}
          </span>
        </>
      )}
    </div>
  </div>
);

const ArtworkCard = ({ artwork }) => (
  <div
    className="flex-shrink-0 w-[140px] sm:w-[180px] snap-start group cursor-pointer"
    data-testid={`artwork-card-${artwork.id}`}
  >
    <div className="relative aspect-square rounded-lg overflow-hidden mb-2 bg-zinc-800">
      {artwork.image_url ? (
        <img
          src={artwork.image_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${artwork.image_url}` : artwork.image_url}
          alt={artwork.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          loading="lazy"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <Palette className="w-8 h-8 text-zinc-600" />
        </div>
      )}
      
      {artwork.likes > 0 && (
        <div className="absolute bottom-2 right-2 px-2 py-0.5 bg-black/60 rounded text-xs text-white flex items-center gap-1">
          <Star className="w-3 h-3 text-orange-400" />
          {artwork.likes}
        </div>
      )}
    </div>
    
    <h3 className="text-sm font-medium text-white truncate group-hover:text-orange-400 transition-colors">
      {artwork.title}
    </h3>
    <p className="text-xs text-zinc-500 truncate">by {artwork.artist}</p>
  </div>
);

const CreatorCard = ({ creator }) => (
  <div
    className="flex-shrink-0 w-[120px] snap-start group text-center cursor-pointer"
    data-testid={`creator-card-${creator.id}`}
  >
    <div className="w-20 h-20 mx-auto rounded-full overflow-hidden mb-2 ring-2 ring-zinc-800 group-hover:ring-orange-500 transition-all bg-zinc-800">
      {creator.avatar_url ? (
        <img
          src={creator.avatar_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${creator.avatar_url}` : creator.avatar_url}
          alt={creator.display_name}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-zinc-500 text-xl font-bold">
          {creator.display_name?.[0] || '?'}
        </div>
      )}
    </div>
    
    <h3 className="text-sm font-medium text-white truncate group-hover:text-orange-400 transition-colors">
      {creator.display_name}
    </h3>
    <p className="text-xs text-zinc-500">{creator.series_count} series</p>
  </div>
);

// ============================================================================
// LOADING SKELETON
// ============================================================================

const LoadingSkeleton = ({ blockType, count = 4 }) => {
  if (blockType === 'featured_creators') {
    return Array(count).fill(0).map((_, i) => (
      <div key={i} className="flex-shrink-0 w-[120px] animate-pulse text-center">
        <div className="w-20 h-20 mx-auto rounded-full bg-zinc-800 mb-2" />
        <div className="h-3 bg-zinc-800 rounded w-3/4 mx-auto mb-1" />
        <div className="h-2.5 bg-zinc-800 rounded w-1/2 mx-auto" />
      </div>
    ));
  }
  if (blockType === 'artwork_gallery') {
    return Array(count).fill(0).map((_, i) => (
      <div key={i} className="flex-shrink-0 w-[180px] animate-pulse">
        <div className="aspect-square bg-zinc-800 rounded-lg mb-2" />
        <div className="h-3.5 bg-zinc-800 rounded w-3/4 mb-1" />
        <div className="h-3 bg-zinc-800 rounded w-1/2" />
      </div>
    ));
  }
  return Array(count).fill(0).map((_, i) => (
    <div key={i} className="flex-shrink-0 w-[160px] animate-pulse" data-testid="loading-skeleton">
      <div className="aspect-[3/4] bg-zinc-800 rounded-lg mb-2" />
      <div className="h-3.5 bg-zinc-800 rounded w-3/4 mb-1" />
      <div className="h-3 bg-zinc-800 rounded w-1/2" />
    </div>
  ));
};

// ============================================================================
// ERROR FALLBACK
// ============================================================================

const ErrorFallback = ({ error, onRetry, blockType }) => (
  <div className="flex items-center gap-3 px-4 sm:px-6 py-4 bg-zinc-900/50 rounded-lg mx-4">
    <AlertCircle className="w-5 h-5 text-zinc-500 flex-shrink-0" />
    <div className="flex-1 min-w-0">
      <p className="text-sm text-zinc-400">Showing sample data</p>
      <p className="text-xs text-zinc-600 truncate">API: {error}</p>
    </div>
    <button
      onClick={onRetry}
      className="flex-shrink-0 p-1.5 text-zinc-500 hover:text-white hover:bg-zinc-800 rounded transition-colors"
      title="Retry"
      data-testid="data-row-retry"
    >
      <RefreshCw className="w-4 h-4" />
    </button>
  </div>
);

// ============================================================================
// DATA ROW BLOCK COMPONENT
// ============================================================================

const DataRowBlock = ({
  blockId,
  blockType,
  props = {},
  isSelected,
  onSelect,
}) => {
  const scrollRef = useRef(null);
  
  const {
    title = 'Content Row',
    source = 'featured',
    tag = '',
    limit = 10,
    cardVariant = 'discovery',
    seeAllLink = '',
    seeAllText = 'See All',
    showRank = false,
    layout = 'carousel',
    columns = 4,
  } = props;
  
  // Central data fetching
  const { data: apiData, loading, error, refetch } = useDataRow({
    blockType,
    source,
    tag,
    limit,
  });
  
  // Use API data if available, else fall back to samples
  const isLive = apiData.length > 0 && !error;
  const data = apiData.length > 0 ? apiData : (loading ? [] : getSampleData(blockType, limit));
  
  // Render card list
  const renderCards = () => {
    if (loading && data.length === 0) {
      return <LoadingSkeleton blockType={blockType} count={Math.min(limit, 6)} />;
    }
    
    if (blockType === 'artwork_gallery') {
      return data.map((artwork) => (
        <ArtworkCard key={artwork.id} artwork={artwork} />
      ));
    }
    
    if (blockType === 'featured_creators') {
      return data.map((creator) => (
        <CreatorCard key={creator.id} creator={creator} />
      ));
    }
    
    // Series / Novel / Trending / New Releases
    return data.map((series, index) => (
      <SeriesCard 
        key={series.id} 
        series={series} 
        rank={showRank ? index + 1 : null}
        variant={cardVariant}
        showRank={showRank}
      />
    ));
  };
  
  // Grid layout for artwork
  if (layout === 'grid' && blockType === 'artwork_gallery') {
    return (
      <section 
        className={cn(
          "py-6 transition-all",
          isSelected && "ring-2 ring-orange-500 ring-offset-2 ring-offset-zinc-950"
        )}
        onClick={() => onSelect?.(blockId)}
        data-testid={`data-row-${blockId}`}
        data-block-id={blockId}
      >
        <SectionHeader title={title} seeAllLink={seeAllLink} seeAllText={seeAllText} isLive={isLive} />
        {error && <ErrorFallback error={error} onRetry={refetch} blockType={blockType} />}
        
        <div 
          className="px-4 sm:px-6 grid gap-4"
          style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
        >
          {renderCards()}
        </div>
      </section>
    );
  }
  
  // Carousel layout (default)
  return (
    <section 
      className={cn(
        "py-6 transition-all",
        isSelected && "ring-2 ring-orange-500 ring-offset-2 ring-offset-zinc-950"
      )}
      onClick={() => onSelect?.(blockId)}
      data-testid={`data-row-${blockId}`}
      data-block-id={blockId}
    >
      <SectionHeader title={title} seeAllLink={seeAllLink} seeAllText={seeAllText} isLive={isLive} />
      {error && <ErrorFallback error={error} onRetry={refetch} blockType={blockType} />}
      
      <ScrollContainer scrollRef={scrollRef}>
        {renderCards()}
      </ScrollContainer>
    </section>
  );
};

export default DataRowBlock;
